import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useGeri } from './lib/geri';
import { Kabuk } from './Kabuk';
import { Pano } from './ekranlar/Pano';
import { IsAc } from './ekranlar/IsAc';
import { IsDetay } from './ekranlar/IsDetay';
import { Kurulu } from './ekranlar/Kurulu';
import { Harita } from './ekranlar/Harita';
import { TanimlarEkrani } from './ekranlar/Tanimlar';
import { Saha } from './ekranlar/Saha';
import { Rapor } from './ekranlar/Rapor';
import { Arsiv } from './ekranlar/Arsiv';
import { Hesabim } from './ekranlar/Hesabim';
import { Aktivite } from './ekranlar/Aktivite';
import { IslerimKuyruk } from './ekranlar/IslerimKuyruk';
import { SifremiUnuttum } from './ekranlar/SifremiUnuttum';
import { ParolaBelirle } from './ekranlar/ParolaBelirle';
import { SahaIsDetay } from './ekranlar/SahaIsDetay';
import { MusteriIsDetay } from './ekranlar/MusteriIsDetay';
import { useOturum } from './lib/oturum';
import { musteriSayfasiMi } from '@mod/domain';
import { useTelefon } from './lib/cihaz';

/**
 * GERÇEK URL'LER.
 *
 * Neden onemli:
 *   - Tarayici GERI tusu calisir (saha kullanicisi icin en dogal gezinme)
 *   - Sayfa yenilenince yer KAYBOLMAZ — zayif baglantida sik oluyor
 *   - Bir isin linki PAYLASILABILIR ("şu işe bak" diye WhatsApp'tan atmak)
 *   - PWA ana ekrandan acildiginda son konum korunur
 *
 * ⚠️ Sunucu tarafinda SPA fallback gerekir: /is/<id> gibi derin bir baglanti
 *    dogrudan acildiginda index.html donmeli (bkz. apps/api spa.controller.ts).
 */

const rootRoute = createRootRoute({
  component: () => (
    <Kabuk>
      <Outlet />
    </Kabuk>
  ),
});

/**
 * ANA EKRAN = CİHAZ × ROL (devir dokümanı §6.11).
 *
 *                    telefon          masaüstü
 *   şef / admin      İşlerim          Pano
 *   saha rolleri     İşlerim          İşlerim
 *
 * ⚠️ Şef telefonda da İŞLERİM görür, panonun küçüğünü DEĞİL:
 *    *"Sahadaki kişinin telefonda yapabileceği tek şey işini yürütmektir.
 *      Analiz, pano, rapor masaüstünde kalır."*
 *    Şefin mobilde işi var (§3.1): yeşil kart / kontrol formu fotoğrafını
 *    sahada yükler — bu da işin adımları üzerinden yapılır, panodan değil.
 */
function AnaEkran() {
  const { ben } = useOturum();
  const telefon = useTelefon();
  const navigate = useNavigate();
  if (!ben) return null;

  /*
   * ⚠️ MÜŞTERİ YETKİLİSİ CİHAZDAN BAĞIMSIZ PANO GÖRÜR. Yukarıdaki tablo
   *    "telefon → İşlerim" diyor ama o kural SAHA kullanıcısı içindir:
   *    müşteri tarafındaki kişinin yürüteceği bir işi yok, telefondan da
   *    baktığı şey aynı özet. `Saha` ekranına düşseydi "sana atanmış aktif
   *    iş yok" boş durumunu görürdü.
   */
  if (ben.musteri) {
    return (
      <Pano
        onIsAc={() => {}}
        onIsSec={(id) => void navigate({ to: '/is/$isId', params: { isId: id } })}
        onRapor={() => void navigate({ to: '/rapor' })}
        onKontrolListesi={() => void navigate({ to: '/kurulu', search: { vade: 'gecti' } })}
      />
    );
  }

  if (telefon || !ben.sefYetkisi) {
    return <Saha onIsSec={(id) => void navigate({ to: '/saha/$isId', params: { isId: id } })} />;
  }
  return (
    <Pano
      onIsAc={() => void navigate({ to: '/is-ac' })}
      onIsSec={(id) => void navigate({ to: '/is/$isId', params: { isId: id } })}
      onRapor={() => void navigate({ to: '/rapor' })}
      // Toplu haftalık kontrol kartı — vadesi geçmiş iskelelerin süzülmüş listesi.
      onKontrolListesi={() => void navigate({ to: '/kurulu', search: { vade: 'gecti' } })}
    />
  );
}

const anaRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: AnaEkran });

const isAcRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/is-ac',
  component: function IsAcSayfa() {
    const navigate = useNavigate();
    return (
      <IsAc
        onKapat={() => void navigate({ to: '/' })}
        onAcildi={() => void navigate({ to: '/' })}
      />
    );
  },
});

const isDetayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/is/$isId',
  component: function IsDetaySayfa() {
    const { isId } = useParams({ from: '/is/$isId' });
    const telefon = useTelefon();
    const { ben } = useOturum();
    // Geldiği yere döner (arama, kurulu liste, pano…) — bkz. lib/geri.ts
    const { etiket, geri } = useGeri('Pano');
    // Müşteri yetkilisi AYRI ve SALT OKUNUR ekranı görür — bkz. MusteriIsDetay.
    if (ben?.musteri) {
      return <MusteriIsDetay id={isId} onGeri={geri} geriEtiket={etiket} />;
    }
    // Masaüstü iş detayı telefonda açılmaz — bildirim/paylaşılan bir bağlantı
    // buraya düşerse mobil akordeon sürümü gösterilir (§6.11).
    return telefon ? (
      <SahaIsDetay id={isId} onGeri={geri} geriEtiket={etiket} />
    ) : (
      <IsDetay id={isId} onGeri={geri} geriEtiket={etiket} />
    );
  },
});

const kuruluRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kurulu',
  /**
   * `?vade=gecti|yaklasiyor` — hatırlatmanın açtığı TOPLU LİSTE görünümü.
   *
   * ⚠️ Bildirim tek tek iş göndermez, kullanıcıyı buraya getirir; adresin
   *    hangi listeyi açacağını taşıması gerekiyor. Geçersiz değer sessizce
   *    yok sayılır — bozuk bir bağlantı ekranı açamamaktansa tam listeyi
   *    göstersin.
   */
  validateSearch: (s: Record<string, unknown>): { vade?: 'gecti' | 'yaklasiyor' } =>
    s.vade === 'gecti' || s.vade === 'yaklasiyor' ? { vade: s.vade } : {},
  component: function KuruluSayfa() {
    const navigate = useNavigate();
    const { vade } = kuruluRoute.useSearch();
    return (
      <Kurulu
        baslangicVade={vade ?? null}
        onIsSec={(id) => void navigate({ to: '/is/$isId', params: { isId: id } })}
      />
    );
  },
});

/**
 * SAHA HARİTASI — şimdilik AYRI SAYFA.
 *
 * ⚠️ Muhtemelen mevcut bir sayfanın üstüne taşınacak (kullanıcı, 12.08.2026).
 *    Bileşen o taşımayı kaldıracak biçimde yazıldı: veri çekme ve durum
 *    `Harita` içinde, dışarıdan yalnız `onIsSec` alıyor.
 */
const haritaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/harita',
  component: function HaritaSayfa() {
    const navigate = useNavigate();
    return <Harita onIsSec={(id) => void navigate({ to: '/is/$isId', params: { isId: id } })} />;
  },
});

const kullanicilarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kullanicilar',
  component: TanimlarEkrani,
});

/** Saha is detayi — mobil akordeon adimlar. Ayri yol, cunku ayri ekran (v9). */
const sahaDetayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/saha/$isId',
  component: function SahaDetaySayfa() {
    const { isId } = useParams({ from: '/saha/$isId' });
    const { etiket, geri } = useGeri('İşlerim');
    return <SahaIsDetay id={isId} onGeri={geri} geriEtiket={etiket} />;
  },
});

const raporRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rapor',
  component: Rapor,
});
const arsivRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/arsiv',
  component: Arsiv,
});
/**
 * /islerim — İKİ AYRI EKRAN, cihaza gore secilir.
 *
 * §6.7 (masaustu)  : sefin UC BASLIKLI aksiyon kuyrugu — is emri fotolari,
 *                    haftalik kontroller, kurulum onaylari.
 * §6.11 (telefon)  : sahadaki kisinin kendi is listesi.
 *
 * Ayni ada sahip iki farkli ekran; dokumanda da oyle. Saha rolu masaustunde de
 * kendi listesini gorur — aksiyon kuyrugu sef/formen isi.
 */
const islerimRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/islerim',
  component: function IslerimSayfa() {
    const { ben } = useOturum();
    const telefon = useTelefon();
    const navigate = useNavigate();
    if (!telefon && ben?.sefYetkisi) {
      return (
        <IslerimKuyruk onIsSec={(id) => void navigate({ to: '/is/$isId', params: { isId: id } })} />
      );
    }
    return <Saha onIsSec={(id) => void navigate({ to: '/saha/$isId', params: { isId: id } })} />;
  },
});

const hesabimRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hesabim',
  component: Hesabim,
});

/**
 * OTURUMSUZ AÇILAN İKİ YOL — bkz. Kabuk `HERKESE_ACIK`.
 *
 * Davet bağlantısına tıklayan kişinin henüz kullanabildiği bir hesabı yoktur;
 * bu ekranlar giriş kapısının DIŞINDA durmak zorunda.
 */
const sifremiUnuttumRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sifremi-unuttum',
  component: function SifremiUnuttumSayfa() {
    const navigate = useNavigate();
    return <SifremiUnuttum onGeri={() => void navigate({ to: '/' })} />;
  },
});

const parolaBelirleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parola-belirle/$jeton',
  component: function ParolaBelirleSayfa() {
    const { jeton } = useParams({ from: '/parola-belirle/$jeton' });
    const navigate = useNavigate();
    return <ParolaBelirle jeton={jeton} onGiris={() => void navigate({ to: '/' })} />;
  },
});

const aktiviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/aktivite',
  component: Aktivite,
});

const routeTree = rootRoute.addChildren([
  anaRoute,
  isAcRoute,
  isDetayRoute,
  kuruluRoute,
  haritaRoute,
  kullanicilarRoute,
  sahaDetayRoute,
  raporRoute,
  arsivRoute,
  islerimRoute,
  hesabimRoute,
  aktiviteRoute,
  sifremiUnuttumRoute,
  parolaBelirleRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: false });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
